import React, { useEffect, useState } from 'react';
import notificationService from '../../../../shared/services/notificationService';

const rowBase = 'flex items-center justify-between border border-gray-200 rounded-lg p-4 bg-white';

const NotificationPreferences = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await notificationService.getPreferences();
      setPrefs(data.preferences);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const validate = (nextPrefs) => {
    if (!nextPrefs.usage_in_app && !nextPrefs.usage_email) {
      return 'Usage: at least one channel must be enabled';
    }
    if (!nextPrefs.updates_in_app && !nextPrefs.updates_email) {
      return 'Updates: at least one channel must be enabled';
    }
    return '';
  };

  const save = async (nextPrefs) => {
    const clientError = validate(nextPrefs);
    if (clientError) {
      setError(clientError);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const data = await notificationService.updatePreferences(nextPrefs);
      setPrefs(data.preferences);
      setSuccess('Preferences saved');
      setTimeout(() => setSuccess(''), 1500);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const onToggle = (field) => {
    if (!prefs) return;
    const next = { ...prefs, [field]: !prefs[field] };
    save(next);
  };

  if (loading) return <div className="text-sm text-[#475569]">Loading notification settings...</div>;
  if (!prefs) return <div className="text-sm text-red-600">{error || 'Unable to load preferences'}</div>;

  return (
    <div className="max-w-3xl space-y-3" style={{ fontFamily: 'Raleway, sans-serif' }}>
      <h2 className="text-xl font-semibold text-[#0f172a]">Notification Preferences</h2>
      <p className="text-sm text-[#475569]">
        Configure how you receive notifications. Limits & Billing notifications are always on for both channels.
      </p>

      <div className={rowBase}>
        <div>
          <div className="font-medium text-[#0f172a]">Usage</div>
          <div className="text-sm text-[#64748b]">Threshold notifications for usage events.</div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!prefs.usage_in_app} onChange={() => onToggle('usage_in_app')} disabled={saving} />
            In-app
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!prefs.usage_email} onChange={() => onToggle('usage_email')} disabled={saving} />
            Email
          </label>
        </div>
      </div>

      <div className={rowBase}>
        <div>
          <div className="font-medium text-[#0f172a]">Limits & Billing</div>
          <div className="text-sm text-[#64748b]">Always enabled for account safety and billing compliance.</div>
        </div>
        <div className="flex items-center gap-4 opacity-80">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked readOnly disabled />
            In-app
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked readOnly disabled />
            Email
          </label>
        </div>
      </div>

      <div className={rowBase}>
        <div>
          <div className="font-medium text-[#0f172a]">Updates</div>
          <div className="text-sm text-[#64748b]">Product updates, maintenance, and platform announcements.</div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!prefs.updates_in_app} onChange={() => onToggle('updates_in_app')} disabled={saving} />
            In-app
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!prefs.updates_email} onChange={() => onToggle('updates_email')} disabled={saving} />
            Email
          </label>
        </div>
      </div>

      {error ? <div className="text-sm text-red-600">{error}</div> : null}
      {success ? <div className="text-sm text-green-600">{success}</div> : null}
    </div>
  );
};

export default NotificationPreferences;
